import { useState } from 'react';
import { matterApi, type MatterDetail } from '../../api';
import Select from '../common/Select';

type Props = {
  matter: MatterDetail;
  onClose: () => void;
  onSaved: () => void;
};

// Rule 67A cost scales (A/B/C). `null` keeps it blank.
const COST_SCALES = [
  { value: '',  label: '— not set —' },
  { value: 'A', label: 'A — Magistrate (≤ R200k)' },
  { value: 'B', label: 'B — Magistrate (> R200k)' },
  { value: 'C', label: 'C — High Court' },
];

export default function EditMatterModal({ matter, onClose, onSaved }: Props) {
  const [matterNameOverride, setMatterNameOverride] = useState(matter.matter_name_override || '');
  const [reference, setReference] = useState(matter.reference || '');
  const [caseNumber, setCaseNumber] = useState(matter.case_number || '');
  const [costScale, setCostScale] = useState(matter.cost_scale || '');
  const [estimatedValue, setEstimatedValue] = useState<string>(
    matter.estimated_value_zar != null ? String(matter.estimated_value_zar) : ''
  );
  const [notes, setNotes] = useState(matter.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!reference.trim()) { setError('Reference is required.'); return; }
    const estNum = estimatedValue.trim() === '' ? null : Number(estimatedValue);
    if (estNum !== null && !Number.isFinite(estNum)) {
      setError('Estimated value must be a number.');
      return;
    }
    setSaving(true); setError(null);
    try {
      await matterApi.update(matter.ROWID, {
        reference: reference.trim(),
        matter_name_override: matterNameOverride.trim() || undefined,
        case_number: caseNumber.trim() || undefined,
        cost_scale: costScale || undefined,
        estimated_value_zar: estNum ?? undefined,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>Edit matter</h3>
        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Reference</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div className="grow">
              <label>Case number</label>
              <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="e.g. 2024/12345" />
            </div>
          </div>
          <div>
            <label>Display name</label>
            <input
              value={matterNameOverride}
              onChange={(e) => setMatterNameOverride(e.target.value)}
              placeholder={matter.matter_name || 'Leave blank to use auto-generated name from parties'}
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Leaving this blank lets the system build the name from the cited parties (e.g. "Smith v Jones &amp; Another").
            </div>
          </div>
          <div className="row">
            <div className="grow">
              <label>Cost scale (Rule 67A)</label>
              <Select
                value={costScale}
                onChange={setCostScale}
                options={COST_SCALES}
              />
            </div>
            <div className="grow">
              <label>Estimated value (ZAR)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label>Notes</label>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 620, maxWidth: '90vw' };
